// Single source of truth for storefront locale detection.
//
// Both the API request interceptor (src/axios.js, sets the `language`
// header) and the UI language (src/i18n/index.js, i18next) MUST resolve
// the browser locale the same way — otherwise the chrome could render in
// one language while the catalogue comes back in another.
//
// Anything not in SUPPORTED_LANGUAGES (including English) resolves to
// `undefined`, which callers treat as "use the default/English behaviour".
//
// NOTE: Additional languages (cy, de, pl, zh, ru, zh-hant) are registered
// in i18n but NOT in SUPPORTED_LANGUAGES, so they're only used in the
// /receipt/ endpoint via URL parameters, not in the main app.
export const SUPPORTED_LANGUAGES = ["ar", "es", "fr", "nl", "pt", "ur"];

// Native names for the language dropdown (Components/LanguageSwitcher.jsx). "en" isn't in
// SUPPORTED_LANGUAGES (it's the default/no-header case) but still needs a dropdown entry.
export const LANGUAGE_DISPLAY_NAMES = {
  en: "English",
  ar: "العربية",
  es: "Español",
  fr: "Français",
  nl: "Nederlands",
  pt: "Português",
  ur: "اردو",
};

const LANGUAGE_STORAGE_KEY = "kayana_web_ordering_language";

/**
 * The customer's own explicit pick from the dropdown, or null if they never chose one (falls
 * back to browser locale). Storage can throw/be unavailable (private mode, blocked site data) --
 * never let that break language resolution.
 */
export function getStoredLanguage() {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
}

/** Pass "en" (or any unsupported code) to clear the override and fall back to browser locale. */
export function setStoredLanguage(language) {
  try {
    if (!language || language === "en") {
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    } else {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
  } catch (e) {
    // Storage unavailable -- the dropdown still works for this page load via i18n.changeLanguage,
    // it just won't survive a refresh.
  }
}

export function resolveLanguage(language = getStoredLanguage() || navigator.language) {
  const primary = (language || "").split("-")[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(primary) ? primary : undefined;
}
